import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Container, Stack } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Loading from '../../../components/common/Loading.component';
import ItemAdd from '../../../components/listItem/create';
import Back from '../../../components/common/Back.component';
import { fetchAPI } from '../../../api/request';
import { API as listAPI } from '../../../module/list/api';
import { API as listItemsInteriorsAPI } from '../../../module/listItemsInteriors/api';
import itemsInteriors from '../../../module/listItemsInteriors';
import {
  AvailableType,
  getOptionsSuppliersLogbooks,
  TAB_MANAGE_LOGBOOK,
  TAB_ORDERED,
  TAB_OVERVIEW,
  TAB_TO_BE_DELIVERED,
} from '../../../module/listItem/conf/Data.conf';
import _ from 'lodash';
import PaperTypography from '../../../components/common/PaperTypography.component';
import Alert from '../../../components/common/Alert.component';
import CardInfoOperation from '../../../components/operations/CardInfoOperation.component';
import EditList from '../../../components/list/edit/EditList.component';
import TabsComponent from '../../../components/common/Tabs.component';
import TabUpdateItemStatus from '../../../components/listItem/tabs/TabUpdateItemStatus.component';
import {
  columnsLogbook,
  columnsProducts,
  HeaderProductsInCollapse,
} from '../../../module/listItemsInteriors/conf/Data.conf';
import interiors from '../../../module/interiors';
import TabCreateLogbook from '../../../components/listItem/tabs/TabCreateLogbook.component';
import TabOrdered from '../../../components/listItem/tabs/TabOrdered.component';
import TabOverview from '../../../components/listItem/tabs/TabOverview.component.js';
import ReceiptIcon from '@mui/icons-material/Receipt';
import RectangleChip from '../../../components/common/RectangleChip.component.js';
import { setTitlePage } from '../../../utility/TitlePage.utility.js';

const ItemsInteriorsListPage = ({ module: { query, detail, code } }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { id, id_operation } = useParams();
  const { jwt } = useSelector((state) => state.login);
  const { state, pathname } = useLocation();
  const { loading: loadingAPI, data } = useSelector(detail.selector.data);

  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(null);
  const [list, setList] = useState(null);
  const [valueTab, setValueTab] = useState(0);
  const [orderedChecked, setOrderedChecked] = useState({});

  const dataNoLogbookGrouped = useMemo(() => {
    const grouped = _.groupBy(
      data?.query,
      (item) => item?.attributes?.available
    );
    return AvailableType?.map((item) => ({
      [item?.id]: grouped?.[item?.id],
    }));
  }, [data?.query]);

  const dataWithLogbookGrouped = useMemo(
    () =>
      Object.values(
        _.groupBy(
          data?.query2,
          (item) => item?.attributes?.id_logbook?.data?.id
        )
      ),
    [data?.query2]
  );

  const optionsSuppliersLogbook = useMemo(
    () =>
      getOptionsSuppliersLogbooks({
        data: data?.query2,
        dataWithLogbookGrouped,
      }),
    [data?.query2, dataWithLogbookGrouped]
  );

  const handleViewOrder = useCallback(
    ({ tab, item }) => {
      if (item?.attributes?.id_logbook?.data?.id) {
        localStorage.setItem(
          'idLogbook',
          item?.attributes?.id_logbook?.data?.id
        );

        setValueTab(tab);
      }
    },
    [setValueTab]
  );

  const optionsTab = useMemo(
    () => [
      {
        id: TAB_TO_BE_DELIVERED,
        label: 'To be delivered',
        children: (
          <TabUpdateItemStatus
            dataGrouped={dataNoLogbookGrouped}
            setSuccess={setSuccess}
            module={{
              ...itemsInteriors,
              list: { conf: { columns: columnsProducts } },
            }}
          />
        ),
      },
      {
        id: TAB_MANAGE_LOGBOOK,
        label: 'Manage Logbook',
        children: (
          <TabCreateLogbook
            dataGrouped={dataNoLogbookGrouped}
            setSuccess={setSuccess}
            optionsSuppliersLogbook={optionsSuppliersLogbook}
            module={{
              ...itemsInteriors,
              list: { conf: { columns: columnsProducts } },
            }}
            moduleDepartment={interiors}
          />
        ),
      },
      {
        id: TAB_ORDERED,
        label: 'Ordered',
        children: !_.isEmpty(data?.query2) ? (
          <TabOrdered
            orderById={
              'attributes.id_logbook.data.attributes.id_supplier.data.attributes.label'
            }
            header={_.concat(columnsLogbook, [
              {
                label: '',
                dataKey: 'download_pdf',
                sortable: false,
                width: '2%',
              },
              {
                label: '',
                dataKey: 'send_of_to_mexal',
                sortable: false,
                width: '2%',
              },
              {
                label: '',
                dataKey: 'send_of',
                sortable: false,
                width: '2%',
              },
            ])}
            data={dataWithLogbookGrouped}
            operationListData={list?.id_operation_list?.data}
            valueTab={valueTab}
            setSuccessUpdate={setSuccess}
            module={itemsInteriors}
            headerCollapse={HeaderProductsInCollapse}
            code={interiors?.code}
            checkedByLogbook={orderedChecked}
            onCheckedByLogbookChange={setOrderedChecked}
          />
        ) : (
          <PaperTypography title={'No items have been added to this section'} />
        ),
      },
      {
        id: TAB_OVERVIEW,
        label: 'Overview',
        children: (
          <TabOverview
            module={itemsInteriors}
            mainTag={interiors.tag}
            viewTag={false}
            onChangeTab={handleViewOrder}
          />
        ),
      },
    ],
    [
      data,
      dataNoLogbookGrouped,
      dataWithLogbookGrouped,
      list?.id_operation_list?.data,
      valueTab,
      optionsSuppliersLogbook,
      handleViewOrder,
      orderedChecked,
    ]
  );

  const onChangeTab = useCallback(
    (value) => {
      setValueTab(value);
    },
    [setValueTab]
  );

  const onError = useCallback((error) => {
    setSuccess(false);
    setLoading(false);
    setError(error);
    // eslint-disable-next-line
  }, []);

  const handleViewLogbook = useCallback(() => {
    const idLogbook =
      list?.id_operation_list?.data?.attributes?.id_delivery_note?.data
        ?.attributes?.id_logbook?.data?.id;
    if (idLogbook) {
      navigate(`/logbook/${idLogbook}`);
    }
  }, [list, navigate]);

  useEffect(() => {
    fetchAPI(listAPI.item, { id }, '', null, jwt)
      .then((resp) => {
        setList(resp?.data?.attributes);
      })
      .catch((error) => onError(error));
    // eslint-disable-next-line
  }, [id, success]);

  useEffect(() => {
    if (
      list !== null &&
      ((error &&
        (error?.response?.status === 404 || error?.response?.status === 500)) ||
        `${list?.id_operation_list?.data?.id}` !== `${id_operation}`)
    ) {
      navigate('/404');
    }
  }, [error, navigate, id_operation, list]);

  useEffect(() => {
    dispatch(
      query.detail.action({
        settings: query.detail.settings, //ITEMS LIST
        token: jwt,
        params: { id },
        query: { populate: query.detail.populate },
        query2: { populate: '*' },
        settings2: listItemsInteriorsAPI.listItemsWithLogbook, //ITEMS IN THE LOGBOOK
      })
    );
    return () => {
      dispatch(
        query.detail.action({
          settings: null,
          token: null,
          params: null,
          query: null,
        })
      );
    };
    // eslint-disable-next-line
  }, [id, success]);

  useEffect(() => {
    const tab = localStorage.getItem('tab');
    if ((tab !== '', parseInt(tab) <= optionsTab?.length)) {
      setValueTab(parseInt(tab));
      localStorage.setItem('tab', '');
    }
  }, [optionsTab]);

  useEffect(() => {
    if (!state?.yachtName) {
      setTitlePage({
        pathname,
        state: {
          yachtName:
            list?.id_operation_list?.data?.attributes?.id_yacht?.data
              ?.attributes?.yacht_name,
          orderType:
            list?.id_operation_list?.data?.attributes?.id_service?.data
              ?.attributes?.service,
        },
      });
    }
  }, [state, list, pathname]);

  return (
    <Fragment>
      <Container disableGutters={true} maxWidth={false} sx={{ height: '100%' }}>
        {error && <Alert severity='error'>{'An error has occurred'}</Alert>}

        <Back title='Back' page='Items List' />
        <CardInfoOperation
          list={list}
          isLoading={loading || loadingAPI}
          otherInfo={[
            {
              id: 'delivery_note_number',
              value: (
                <Box
                  onClick={() => handleViewLogbook()}
                  sx={{ cursor: 'pointer' }}
                >
                  {
                    list?.id_operation_list?.data?.attributes?.id_delivery_note
                      ?.data?.attributes?.delivery_note_number
                  }
                </Box>
              ),
              icon: <ReceiptIcon style={{ width: 16, height: 16 }} />,

              avatar: {
                sx: {
                  color: '#005488',
                  opacity: 0.9,
                },
              },
            },
            {
              id: 'service',
              value: (
                <RectangleChip
                  value={
                    list?.id_operation_list?.data?.attributes?.id_service?.data
                      ?.attributes?.service
                  }
                  width={'auto'}
                  backgroundColor={'#009ab6'}
                  color='#fff'
                />
              ),

              avatar: {
                sx: {
                  color: '#005488',
                  opacity: 0.9,
                },
              },
            },
          ]}
        >
          <EditList item={list} department={code} setSuccessEdit={setSuccess} />
        </CardInfoOperation>
        {loading || loadingAPI ? (
          <Loading />
        ) : _.isEmpty(data?.query) && _.isEmpty(data?.query2) ? (
          <Stack direction={'row'} spacing={2} mt={2}>
            <ItemAdd
              setSuccessAdd={setSuccess}
              module={{
                ...itemsInteriors,
                create: { conf: itemsInteriors.create.conf.default },
              }}
              viewIconAdd={true}
            />
            <Box width={'95%'}>
              <PaperTypography title={'Add Items to list'} />
            </Box>
          </Stack>
        ) : (
          <Stack mt={4}>
            <TabsComponent
              sx={{ marginTop: 2 }}
              onChangeTab={onChangeTab}
              defaultTab={valueTab}
              tabs={optionsTab}
            />
          </Stack>
        )}
      </Container>
    </Fragment>
  );
};

export default ItemsInteriorsListPage;
